import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useServices } from "@/services-context";
import arrowOutlinedRight from "@/assets/arrow-outlined-right.svg";
import styles from "./cart-page.desktop.module.scss";
import CartItem from "@/elements-react/cart-item";
import PriceValue from "@/elements-react/price-value";
import { FixButton } from "@/elements-react/fix-button";
import { Icon } from "@/elements-react/fix-icon";
import CartEmptyLabel from "@/elements-react/cart-empty-label";
import { FooterElement } from "@/elements-react/footer-element/FooterElement.tsx";
import { FixAlert } from "@/elements-react/fix-alert";
import { FixDivider } from "@/elements-react/fix-divider";
import { CART_LISTENER_NAME } from "@/context/cart.ts";
import { useCartCommons } from "./cart-commons";
import { DocumentElements } from "@/elements-react/document-elements/document-elements";
import {Header} from "@/elements-react/catalog-layout/header";

const CartPageDesktop = () => {
  const { t } = useTranslation();
  const services = useServices([CART_LISTENER_NAME]);
  const { cart } = services;

  const availableProducts = useMemo(() => {
    const items = cart?.items?.filter((item) => !item.isDisabled && !item.baseCartProduct) ?? [];
    return items.map((item) => {
      const additionalItem = cart?.items?.find((i) => i.baseCartProduct && i.baseCartProduct.id === item.id);
      return { baseItem: item, additionalItem };
    });
  }, [cart.items]);

  const disabledProducts = useMemo(() => cart.items?.filter((item) => item.isDisabled) ?? [], [cart]);
  const { goToCheckout } = useCartCommons({});

  return (
    <div>
      <DocumentElements />
      <div className={classNames(styles.root)}>
        <Header simple={true} />
        {cart.items?.length ? (
          <div className={styles["cart-container"]}>
            <div className={styles["cart-items-container"]}>
              <div className={styles.alerts}>
                <FixAlert />
              </div>
              <div className={styles["cart-items-total"]}>
                <h1 className={styles.label}>{t("zxjlya047dy77p7w.cart", "Cart")}</h1>
                <h2 className={styles["sub-title"]}>{t("r7p16j1hyjl2luuk.products-count", "{{count}} products", { count: cart.items?.length ?? 0 })}</h2>
              </div>
              <div className={styles.products}>
                {availableProducts.map((item, key) =>
                  item.additionalItem ? (
                    <div className={styles["items-join"]} key={key}>
                      <CartItem product={item.baseItem} singleProduct={false} />
                      <FixDivider />
                      <CartItem product={item.additionalItem} singleProduct={false} />
                    </div>
                  ) : (
                    <CartItem product={item.baseItem} key={key} />
                  ),
                )}
                {disabledProducts.length > 0 && (
                  <div className={styles["disabled-products-divide"]}>
                    {t("wcvjoidfu38gd.disabled-products-label", "Sorry, but these products are no longer available for purchase")}
                  </div>
                )}
                {disabledProducts.map((item, key: number) => (
                  <Fragment key={key}>
                    {key > 0 && <FixDivider />}
                    <CartItem product={item} />
                  </Fragment>
                ))}
              </div>
            </div>
            <div className={styles["cart-totals"]}>
              <div className={styles.label}>{t("ziihw8w2uag97uj9.order-summary", "Order summary")}</div>
              <div className={styles.info}>
                <div className={styles.total}>
                  <span>{t("sor9ad5qli79zyjd.subtotal", "Subtotal:")}</span>
                  <span>
                    <PriceValue className={styles.price} amount={Number(cart.subtotal?.amount)} />
                  </span>
                </div>
                <div className={styles.total}>
                  <span>{t("76f9o8307ucn7hsp.tax", "Tax")}:</span>
                  <span>
                    <PriceValue className={styles.price} amount={Number(cart.taxTotal?.amount)} />
                  </span>
                </div>
                <div className={styles.subtotal}>
                  <span>{t("92cpzygezfidub60.total", "Total")}:</span>
                  <span className={styles["total-price"]}>
                    <PriceValue className={styles.price} amount={Number(cart.grandTotal?.amount)} />
                  </span>
                </div>
              </div>
              <FixDivider />
              <div className={styles.total}>
                <span>{t("c9isaps5u0422u26.total-weight", "Total weight")}:</span>
                <span>
                  {cart.totalWeight} {t("h19j26yhh5dxubcv.kg", "kg")}
                </span>
              </div>
              <FixButton
                className={classNames(styles["checkout-btn"])}
                variant="primary"
                size="large"
                onClick={goToCheckout}
                router-ignore="true"
              >
                <Icon src={arrowOutlinedRight} className={styles["cart-button-icon"]} />
                {t("vx7hvi0v1mex3f6o.proceed-to-checkout", "Proceed to checkout")}
              </FixButton>
            </div>
          </div>
        ) : (
          <CartEmptyLabel />
        )}
      </div>
      <FooterElement />
    </div>
  );
};

export default CartPageDesktop;
